/**
 * AddSlotModal
 * -------------
 * Collects the minimum details required to create an admin booking for a specific
 * room/time slot combination.
 */
import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  computeSlotLabelAvailability,
  generateAutoSlotLabels,
  parseSlotLabelsInput,
} from '../utils/slotLabelUtils';
import { getEffectiveRemainingCapacity } from '../utils/capacityUtils';

const BOOKING_STATUS_OPTIONS = ['confirmed', 'pending', 'hold', 'cancelled'];

const defaultFormValues = {
  customerName: '',
  customerEmail: '',
  customerPhoneNumber: '',
  partySize: 1,
  slotLabels: '',
  specialNote: '',
  status: 'confirmed',
};

const AddSlotModal = ({
  open,
  onClose,
  room,
  cell,
  slotDisplayTime,
  timeSlotValue,
  date,
  onSubmit,
  isSubmitting,
  error,
  slotLabelSuggestions = [],
}) => {
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [didInitializeForm, setDidInitializeForm] = useState(false);

  const { slotLabelPool, availableSlotLabels, reservedSlotLabels } = useMemo(
    () => computeSlotLabelAvailability({ cell, slotLabelSuggestions }),
    [cell, slotLabelSuggestions]
  );

  const availableCapacity = useMemo(
    () => getEffectiveRemainingCapacity(cell),
    [cell]
  );

  const maxPartySize = availableCapacity;
  const currentPartySize = formValues.partySize;

  const partySizeHelperText =
    maxPartySize === null
      ? undefined
      : maxPartySize > 0
      ? `Up to ${maxPartySize} smashers remain for this slot.`
      : 'No remaining capacity for this slot.';

  const partySizeInputProps = useMemo(() => {
    const props = { min: 1 };
    if (maxPartySize !== null && maxPartySize > 0) {
      props.max = maxPartySize;
    }
    return props;
  }, [maxPartySize]);

  useEffect(() => {
    if (!open) {
      setDidInitializeForm(false);
      return;
    }

    if (didInitializeForm) {
      return;
    }

    const boundedInitialPartySize =
      maxPartySize === null
        ? defaultFormValues.partySize
        : maxPartySize > 0
        ? Math.min(defaultFormValues.partySize, maxPartySize)
        : '';

    setFormValues({
      ...defaultFormValues,
      partySize: boundedInitialPartySize,
      slotLabels:
        boundedInitialPartySize && typeof boundedInitialPartySize === 'number'
          ? generateAutoSlotLabels({
              partySize: boundedInitialPartySize,
              availableSlotLabels,
              slotLabelPool,
              reservedSlotLabels,
            })
          : '',
    });
    setDidInitializeForm(true);
  }, [
    open,
    didInitializeForm,
    maxPartySize,
    availableSlotLabels,
    slotLabelPool,
    reservedSlotLabels,
  ]);

  useEffect(() => {
    if (!open || maxPartySize === null || !currentPartySize) {
      return;
    }
    if (currentPartySize <= maxPartySize) {
      return;
    }

    setFormValues((prev) => {
      const boundedPartySize = Math.min(prev.partySize || 0, maxPartySize);
      const preferredSlotLabels = parseSlotLabelsInput(prev.slotLabels);
      const nextSlotLabels = boundedPartySize
        ? generateAutoSlotLabels({
            partySize: boundedPartySize,
            availableSlotLabels,
            slotLabelPool,
            reservedSlotLabels,
            preferredSlotLabels,
          })
        : '';
      return {
        ...prev,
        partySize: boundedPartySize,
        slotLabels: nextSlotLabels,
      };
    });
  }, [
    open,
    maxPartySize,
    currentPartySize,
    availableSlotLabels,
    slotLabelPool,
    reservedSlotLabels,
  ]);

  const handleDialogClose = (_, reason) => {
    if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    onClose();
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;

    if (field === 'partySize') {
      setFormValues((prev) => {
        if (value === '') {
          return {
            ...prev,
            partySize: '',
            slotLabels: '',
          };
        }

        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return prev;
        }
        const upperBound =
          maxPartySize === null ? numericValue : Math.min(numericValue, maxPartySize);
        const nextPartySize = upperBound > 0 ? upperBound : '';

        const preferredSlotLabels = parseSlotLabelsInput(prev.slotLabels);
        const nextSlotLabels = nextPartySize
          ? generateAutoSlotLabels({
              partySize: nextPartySize,
              availableSlotLabels,
              slotLabelPool,
              reservedSlotLabels,
              preferredSlotLabels,
            })
          : '';

        return {
          ...prev,
          partySize: nextPartySize,
          slotLabels: nextPartySize ? nextSlotLabels : '',
        };
      });
      return;
    }

    if (field === 'slotLabels') {
      setFormValues((prev) => ({
        ...prev,
        slotLabels: value,
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formValues);
  };

  const availableCapacityDisplay = availableCapacity;

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>Add booking</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {cell?.isBlocked && (
              <Alert severity="warning">
                This slot is currently blocked. Saving a booking will override the block status.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2">
              Room: <strong>{room?.name || 'Unknown room'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Date: {date || 'N/A'} — Time: {slotDisplayTime || timeSlotValue || 'TBD'}
            </Typography>
            {availableCapacityDisplay !== null && (
              <Typography variant="caption" color="text.secondary">
                Remaining capacity: {availableCapacityDisplay}
              </Typography>
            )}
            <TextField
              label="Customer name"
              value={formValues.customerName}
              onChange={handleChange('customerName')}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Customer email"
              value={formValues.customerEmail}
              onChange={handleChange('customerEmail')}
              type="email"
              fullWidth
            />
            <TextField
              label="Customer phone"
              value={formValues.customerPhoneNumber}
              onChange={handleChange('customerPhoneNumber')}
              fullWidth
            />
            <TextField
              label="Party size"
              value={formValues.partySize}
              onChange={handleChange('partySize')}
              type="number"
              inputProps={partySizeInputProps}
              helperText={partySizeHelperText}
              required
              fullWidth
            />
            <TextField
              label="Slot labels (comma separated)"
              value={formValues.slotLabels}
              onChange={handleChange('slotLabels')}
              helperText={
                slotLabelSuggestions.length
                  ? `Suggestions: ${slotLabelSuggestions.join(', ')}`
                  : 'Optional'
              }
              fullWidth
            />
            <TextField
              label="Booking status"
              value={formValues.status}
              onChange={handleChange('status')}
              select
              fullWidth
            >
              {BOOKING_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </TextField>
            <TextField
              label="Special note"
              value={formValues.specialNote}
              onChange={handleChange('specialNote')}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save booking'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddSlotModal;